import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { danger } from 'redux-store/reducers/common-reducer';
import { initCloudinaryStreaming } from 'redux-store/reducers/cloudinaryVideos';

import { createReducer } from 'utils/redux';
import { API, get, post } from 'utils/api';
import { CAMPAIGN_ASSET_IMAGE, CAMPAIGN_ASSET_VIDEO } from 'utils/campaignAsset';
import { __t, applyTranslations } from 'utils/language';
import { logError } from 'utils/log';
import { API_CODE_CONTENT_TYPE_IS_NOT_SUPPORTED } from 'utils/status';
import { addTempIds, uploadImages as uploadImagesPdfs, uploadVideos } from 'utils/upload-asset';

import type { Action, CampaignAsset, Dispatch, ImageStandard, NewCampaignAsset, ThunkAction } from 'types';
import { GetState } from 'types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./inspirationalMaterial.i18n.json'));

const { IMAGES } = API.END_POINTS.API;

const ADD_MATERIALS = 'inspirationalMaterials/ADD_MATERIALS';
const REMOVE_MATERIAL = 'inspirationalMaterials/REMOVE_MATERIAL';
const RESET_MATERIALS = 'inspirationalMaterials/RESET_MATERIALS';
const SET_MATERIALS = 'inspirationalMaterials/SET_MATERIALS';

type State = {
  assets: Array<CampaignAsset>,
  isUpdated: boolean
};

const initialState: State = {
  assets: [],
  isUpdated: false
};

export function resetMaterials(): Action {
  return {
    type: RESET_MATERIALS
  };
}

export function removeMaterial(asset: CampaignAsset): Action {
  return {
    type: REMOVE_MATERIAL,
    payload: {
      asset
    }
  };
}

function addMaterials(assets: Array<CampaignAsset>): Action {
  return {
    type: ADD_MATERIALS,
    payload: {
      assets
    }
  };
}

export function setMaterials(assets: Array<CampaignAsset>): Action {
  return {
    type: SET_MATERIALS,
    payload: {
      assets
    }
  };
}

export function addMaterialLinks(links: Array<string>): ThunkAction {
  return (dispatch: Dispatch) => {
    const assets: Array<CampaignAsset> = links.map(link => ({
      id: '--',
      assetType: CAMPAIGN_ASSET_VIDEO,
      url: link
    }));

    const newAssets = addTempIds(assets);
    dispatch(addMaterials(newAssets));
  };
}

function uploadBlob(blob: Blob, origin: string): Promise<NewCampaignAsset> {
  const url = IMAGES;
  const data = new FormData();

  data.append('file', blob);

  return post(url, { data })
    .then((response) => ({
      meta: {
        size: blob.size,
        origin
      },
      assetType: CAMPAIGN_ASSET_IMAGE,
      url: response.data.url
    } as NewCampaignAsset));
}

export function addInstagramImage(postImageUrl: string): ThunkAction {
  return async (dispatch: Dispatch) => {
    dispatch(showLoading(postImageUrl));

    try {
      const response = await get(
        postImageUrl, {
          responseType: 'blob'
        }, {
          suppressAuth: true,
          suppressCollabHeaders: true
        }
      );

      const asset = await uploadBlob(response.data, postImageUrl);
      const newAssets = addTempIds([asset]);
      dispatch(addMaterials(newAssets));
    } catch (err) {
      dispatch(danger(__t('inspirational_materials_reducer.instagram_add_fail_message')));
      logError(err);
    } finally {
      dispatch(hideLoading(postImageUrl));
    }
  };
}

export function addImage(image: ImageStandard): ThunkAction {
  return (dispatch: Dispatch) => {
    const asset: CampaignAsset = {
      id: '--',
      assetType: CAMPAIGN_ASSET_IMAGE,
      url: image.assetUrl
    };

    const newAssets = addTempIds([asset]);
    dispatch(addMaterials(newAssets));
  };
}

export function uploadAssets(assets: Array<File>) {
  const url = IMAGES;
  const imagesPdfs = assets.filter(asset => asset.type.includes('image') || asset.type.includes('pdf'));
  const videos = assets.filter(asset => asset.type.includes('video'));
  const promises: Array<Promise<any>> = [];

  return (dispatch: Dispatch, getState: GetState) => {
    const { runtimeConfig } = getState();
    dispatch(showLoading(url));

    if (imagesPdfs.length) {
      promises.push(uploadImagesPdfs(imagesPdfs));
    }

    if (videos.length) {
      promises.push(uploadVideos(videos, Boolean(runtimeConfig.config.userS3VideoUpload)));
    }

    return Promise.all(promises)
      .then((uploadedAssets) => {
        const combinedAssets = uploadedAssets.reduce((prevValue, item) => [...prevValue, ...item], []);
        const newAssets = addTempIds(combinedAssets);

        dispatch(addMaterials(newAssets));
        if (runtimeConfig.config.userS3VideoUpload) {
          dispatch(initCloudinaryStreaming(newAssets));
        }

        return uploadedAssets;
      })
      .catch((error) => {
        logError(error);

        if (error?.response?.data?.code === API_CODE_CONTENT_TYPE_IS_NOT_SUPPORTED) {
          dispatch(danger(__t('collabary_common.errors.content_type_is_not_supported')));
        } else {
          dispatch(danger(error.message || __t('inspirational_materials_reducer.asset_upload_fail_message')));
        }
      })
      .finally(() => {
        dispatch(hideLoading(url));
      });
  };
}

export default createReducer<State, any>('inspirationalMaterials', initialState, {
  [RESET_MATERIALS]: () => initialState,
  [ADD_MATERIALS]: (state, { assets }) => ({
    ...state,
    assets: [...state.assets, ...assets],
    isUpdated: true
  }),
  [SET_MATERIALS]: (state, { assets }) => ({
    ...state,
    assets,
    isUpdated: true
  }),
  [REMOVE_MATERIAL]: (state, { asset }) => ({
    ...state,
    assets: state.assets.filter(a => a.id !== asset.id),
    isUpdated: true
  })
});
